import React, { createContext, useContext, useEffect, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';

interface GoogleMapsContextType {
  isLoaded: boolean;
  loadError: string | null;
  googleMaps: typeof google.maps | null;
}

const GoogleMapsContext = createContext<GoogleMapsContextType>({
  isLoaded: false,
  loadError: null,
  googleMaps: null,
});

export const useGoogleMaps = () => {
  const context = useContext(GoogleMapsContext);
  if (!context) {
    throw new Error('useGoogleMaps must be used within a GoogleMapsProvider');
  }
  return context;
};

interface GoogleMapsProviderProps {
  children: React.ReactNode;
  apiKey: string;
}

export const GoogleMapsProvider: React.FC<GoogleMapsProviderProps> = ({ children, apiKey }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [googleMaps, setGoogleMaps] = useState<typeof google.maps | null>(null);

  useEffect(() => {
    // Only initialize if we have an API key
    if (!apiKey) {
      setLoadError('Google Maps API key not provided');
      return;
    }

    // Check if Google Maps is already loaded
    if (window.google && window.google.maps && window.google.maps.Map) {
      console.log('Google Maps already loaded, using existing instance');
      setIsLoaded(true);
      setGoogleMaps(window.google.maps);
      return;
    }

    console.log('Loading Google Maps...');
    const loader = new Loader({
      apiKey,
      version: 'weekly',
      libraries: ['places'],
    });

    loader
      .load()
      .then((google) => {
        console.log('Google Maps loaded successfully');
        setGoogleMaps(google.maps);
        setIsLoaded(true);
        setLoadError(null);
      })
      .catch((error) => {
        console.error('Failed to load Google Maps:', error);
        setLoadError(error.message || 'Failed to load Google Maps');
        setIsLoaded(false);
      });
  }, [apiKey]);

  const contextValue: GoogleMapsContextType = {
    isLoaded,
    loadError,
    googleMaps,
  };

  return (
    <GoogleMapsContext.Provider value={contextValue}>
      {children}
    </GoogleMapsContext.Provider>
  );
};
